import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { SectionHeading } from "@/components/Atoms";
import { Coins } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { PackOpeningModal } from "@/components/PackOpeningModal";
import { SingleAcquireModal } from "@/components/SingleAcquireModal";
import { TopUpCredits } from "@/components/TopUpCredits";

export default function RedeemPage() {
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [creditsResult, setCreditsResult] = useState(null);
  const [packResult, setPackResult] = useState(null);
  const [cardResult, setCardResult] = useState(null);
  const { refresh } = useAuth();

  const closeAll = () => {
    setCreditsResult(null);
    setPackResult(null);
    setCardResult(null);
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!code.trim() || loading) return;
    setLoading(true);
    closeAll();
    try {
      const r = await api.post("/codes/redeem", { code });
      const data = r.data;
      ["my-cards", "my-cards-adv", "admin-codes", "admin-templates"].forEach((key) => qc.invalidateQueries({ queryKey: [key] }));
      refresh();
      setCode("");
      if (data.type === "credits") {
        setCreditsResult(data);
        toast.success(`+${data.amount.toLocaleString()} credits`);
      } else if (data.type === "pack") {
        const cards = data.cards || (data.card ? [data.card] : []);
        setPackResult({ cards, packName: data.pack_name });
        toast.success(`Pack opened: ${cards.length} card${cards.length === 1 ? "" : "s"}`);
      } else if (data.type === "token") {
        toast.success(`Added to your inventory: ${data.qty}× ${data.name}`);
      } else {
        setCardResult(data.card);
        toast.success(`Card unlocked: ${data.card.title}`);
      }
    } catch (err) {
      toast.error(apiError(err, "Redemption failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <SectionHeading
        eyebrow="Claim"
        title="REDEEM CODE"
        description="Enter a code to receive credits, unlock a specific card, or open a pack."
      />

      <form onSubmit={submit} className="hud-pill rounded-2xl p-6 flex flex-col sm:flex-row gap-3 items-stretch">
        <div className="flex-1">
          <Input
            data-testid="redeem-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="E.G. WELCOME-1000"
            className="bg-[hsl(220_18%_12%)] border-white/10 font-mono uppercase tracking-[0.2em] h-12 text-base"
          />
        </div>
        <Button data-testid="redeem-submit-button" type="submit" disabled={loading} className="bg-cyan-400 text-black hover:bg-cyan-300 h-12 px-6">
          {loading ? "Redeeming…" : "Redeem"}
        </Button>
      </form>

      {creditsResult && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 hud-pill rounded-2xl p-8 text-center"
        >
          <div className="h-16 w-16 mx-auto rounded-2xl bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center shadow-[0_0_40px_rgba(255,210,90,0.5)] mb-4">
            <Coins className="h-8 w-8 text-black" />
          </div>
          <div className="font-display text-4xl text-amber-300 tracking-[0.08em]">+{creditsResult.amount.toLocaleString()} CREDITS</div>
          <div className="font-mono text-xs text-white/50 mt-2">Balance: {creditsResult.credits_balance.toLocaleString()}</div>
        </motion.div>
      )}

      {cardResult && (
        <SingleAcquireModal
          card={cardResult}
          headline="CARD UNLOCKED"
          onClose={() => setCardResult(null)}
        />
      )}

      {packResult && (
        <PackOpeningModal
          cards={packResult.cards}
          packName={packResult.packName}
          onClose={() => setPackResult(null)}
        />
      )}

      <div className="mt-10">
        <TopUpCredits />
      </div>

      <div className="mt-6 hud-pill rounded-2xl p-5 sm:p-6" data-testid="payment-policy">
        <h3 className="font-display text-xl tracking-[0.04em] text-white mb-2">Payment methods & policy</h3>
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-sm text-white/70">Crypto (NOWPayments)</span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#3D95CE]/30 bg-[#3D95CE]/10 px-3 py-1 text-sm text-[#3D95CE]">Venmo · @Playden-INC</span>
        </div>
        <div className="space-y-2 text-sm text-white/60 leading-relaxed">
          <p><span className="text-amber-300 font-semibold">All sales are final — there are no refunds.</span> Credits are virtual and granted once payment is confirmed.</p>
          <p>Incomplete transfers or incorrect amounts will <span className="text-white/80">not</span> constitute payment. If something goes wrong we'll gladly work with you — but everything is set up specifically so there should be no issue. Always include the reference code shown after you request a top-up so we can match your payment quickly.</p>
        </div>
      </div>
    </div>
  );
}
